import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { attendanceApi } from '../api/client';

export function useAttendanceToday() {
  return useQuery({
    queryKey: ['attendanceToday'],
    queryFn: async () => {
      const response = await attendanceApi.getToday();
      return response.data;
    },
    refetchInterval: 30_000,
  });
}

// Named useAttendance* (not useLogin/useLogout) to avoid colliding with the
// unrelated account-auth hooks of the same name in useAuth.ts.
export function useAttendanceLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => attendanceApi.login(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendanceToday'] });
      // Prefix match (no exact:false needed, that's the default) — catches
      // every param variant of the history query (today's, this week's,
      // this month's) that the new dashboard widgets depend on.
      queryClient.invalidateQueries({ queryKey: ['attendanceHistory'] });
    },
  });
}

export function useAttendanceBreak() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => attendanceApi.startBreak(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendanceToday'] });
      // Prefix match (no exact:false needed, that's the default) — catches
      // every param variant of the history query (today's, this week's,
      // this month's) that the new dashboard widgets depend on.
      queryClient.invalidateQueries({ queryKey: ['attendanceHistory'] });
    },
  });
}

export function useAttendanceResume() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => attendanceApi.resume(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendanceToday'] });
      // Prefix match (no exact:false needed, that's the default) — catches
      // every param variant of the history query (today's, this week's,
      // this month's) that the new dashboard widgets depend on.
      queryClient.invalidateQueries({ queryKey: ['attendanceHistory'] });
    },
  });
}

export function useAttendanceLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => attendanceApi.logout(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendanceToday'] });
      // Prefix match (no exact:false needed, that's the default) — catches
      // every param variant of the history query (today's, this week's,
      // this month's) that the new dashboard widgets depend on.
      queryClient.invalidateQueries({ queryKey: ['attendanceHistory'] });
    },
  });
}

export function useAttendanceHistory(params: {
  userId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
} = {}) {
  return useQuery({
    queryKey: ['attendanceHistory', params],
    queryFn: async () => {
      const response = await attendanceApi.getHistory(params);
      return response.data;
    },
  });
}
